
import { useRef, memo, startTransition, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import EmailList from '@/components/email/EmailList';
import { useEmail } from '@/context/EmailContext';
import { DataPagination } from '@/components/ui/data-pagination';

interface EmailFolderContentProps {
  currentFolder: string;
  isDbReady: boolean;
  selectedEmails: string[];
  setSelectedEmails: Dispatch<SetStateAction<string[]>>;
}

const EmailFolderContent = memo(({
  currentFolder,
  isDbReady,
  selectedEmails,
  setSelectedEmails,
}: EmailFolderContentProps) => {
  const { emails, loading, loadFolderEmails, totalPages, totalEmailsInFolder } = useEmail();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get pagination + search state from URL params, with defaults
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '25', 10);
  const searchQuery = searchParams.get('q') || '';

  // Optimistically compute pages from total items and current pageSize to avoid stale disables
  const computedTotalPages = Math.max(1, Math.ceil((totalEmailsInFolder || 0) / (pageSize || 1))) || 1;

  // Track previous folder to reset page
  const previousFolder = useRef(currentFolder);

  // Reset to page 1 when folder changes (search query is preserved)
  useEffect(() => {
    if (previousFolder.current !== currentFolder) {
      previousFolder.current = currentFolder;
      const next: Record<string, string> = { page: '1', pageSize: pageSize.toString() };
      if (searchQuery) next.q = searchQuery;
      setSearchParams(next, { replace: true });
    }
  }, [currentFolder, pageSize, searchQuery, setSearchParams]);

  // Load emails when page, pageSize, folder, or search changes
  useEffect(() => {
    if (isDbReady) {
      loadFolderEmails(currentFolder, pageSize, currentPage, searchQuery || undefined);
    }
  }, [currentPage, pageSize, currentFolder, searchQuery, isDbReady, loadFolderEmails]);

  const handleSelectEmail = (id: string, selected: boolean) => {
    startTransition(() => {
      if (selected) {
        setSelectedEmails(prev => [...prev, id]);
      } else {
        setSelectedEmails(prev => prev.filter((emailId) => emailId !== id));
      }
    });
  };

  const handleSelectAll = (selected: boolean) => {
    startTransition(() => {
      if (selected) {
        setSelectedEmails(emails.map((email) => email.id) || []);
      } else {
        setSelectedEmails([]);
      }
    });
  };

  const handlePageChange = (page: number) => {
    const next: Record<string, string> = { page: page.toString(), pageSize: pageSize.toString() };
    if (searchQuery) next.q = searchQuery;
    setSearchParams(next, { replace: true });
    setSelectedEmails([]); // Clear selection on page change
  };

  const handlePageSizeChange = (newSize: number) => {
    const next: Record<string, string> = { page: '1', pageSize: newSize.toString() };
    if (searchQuery) next.q = searchQuery;
    setSearchParams(next, { replace: true });
    setSelectedEmails([]); // Clear selection
  };

  if (!isDbReady) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Initializing database...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && emails.length === 0) {
    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading emails...</p>
            <p className="text-xs text-muted-foreground mt-2">Debug: isDbReady={isDbReady.toString()}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-muted/10">
      <div className="flex-1 overflow-auto">
        <div className="px-4 py-2 border-b bg-background/50 text-xs text-muted-foreground">
          {searchQuery
            ? `${totalEmailsInFolder} result${totalEmailsInFolder !== 1 ? 's' : ''} for "${searchQuery}"`
            : `Showing ${emails.length} email${emails.length !== 1 ? 's' : ''}`}
        </div>
        <EmailList
          emails={emails}
          folder={currentFolder}
          selectedEmails={selectedEmails}
          onSelectEmail={handleSelectEmail}
          onSelectAll={handleSelectAll}
        />
      </div>
      
      {totalEmailsInFolder > 0 && (
        <DataPagination
          currentPage={currentPage}
          totalPages={computedTotalPages}
          pageSize={pageSize}
          totalItems={totalEmailsInFolder}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 25, 50, 100]}
          className="mt-0"
        />
      )}
    </div>
  );
});

EmailFolderContent.displayName = 'EmailFolderContent';

export default EmailFolderContent;
